import React, { useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useGraphEditorStore } from '@/graph-store';
import type { IOMapping } from '@/graph/domain/types';

interface IOMappingEditorProps {
  inputIds: string[];
  outputIds: string[];
  mapping: IOMapping[];
  onChange: (mapping: IOMapping[]) => void;
}

/**
 * Matrix editor where rows = inputs, columns = outputs.
 * Each cell holds a fraction (0–1). Empty means no link.
 */
export function IOMappingEditor({
  inputIds,
  outputIds,
  mapping,
  onChange,
}: IOMappingEditorProps) {
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);

  const getFraction = useCallback(
    (inputId: string, outputId: string): string => {
      const entry = mapping.find(
        (m) => m.inputMaterialId === inputId && m.outputMaterialId === outputId,
      );
      return entry ? String(entry.fraction) : '';
    },
    [mapping],
  );

  const handleChange = useCallback(
    (inputId: string, outputId: string, raw: string) => {
      const filtered = mapping.filter(
        (m) => !(m.inputMaterialId === inputId && m.outputMaterialId === outputId),
      );
      const num = parseFloat(raw);
      if (!isNaN(num) && num > 0 && num <= 1) {
        filtered.push({
          inputMaterialId: inputId,
          outputMaterialId: outputId,
          fraction: num,
        });
      }
      onChange(filtered);
    },
    [mapping, onChange],
  );

  const matName = (id: string) => materialNodes[id]?.name ?? id;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 200 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Input \ Output</TableCell>
            {outputIds.map((oId) => (
              <TableCell key={oId} align="center" sx={{ fontWeight: 600, fontSize: 12 }}>
                {matName(oId)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {inputIds.map((iId) => (
            <TableRow key={iId}>
              <TableCell sx={{ fontSize: 12 }}>{matName(iId)}</TableCell>
              {outputIds.map((oId) => (
                <TableCell key={oId} align="center" sx={{ p: 0.5 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={getFraction(iId, oId)}
                    onChange={(e) => handleChange(iId, oId, e.target.value)}
                    slotProps={{ htmlInput: { min: 0, max: 1, step: 0.05 } }}
                    sx={{ width: 70, '& input': { textAlign: 'center', fontSize: 12, p: 0.5 } }}
                    placeholder="—"
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Enter fraction (0–1) for each input → output link. Leave empty for no link.
      </Typography>
    </Box>
  );
}
